import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  Flex,
  HStack,
  VStack,
  Text,
  Icon,
  useColorModeValue,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Divider,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Spinner,
  Center,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaArrowLeft,
  FaChevronRight,
  FaMoneyBillWave,
  FaUsers,
} from "react-icons/fa";
import useAxios from "axios-hooks";
import { useSetRecoilState } from "recoil";
import { toastState } from "atoms/toastState";
import logger from "infra/logger";
import PayrunPayoutsTable from "../components/PayrunPayoutsTable";
import PayrunExportButton from "../components/PayrunExportButton";

const MotionBox = motion(Box);

const GlassMorphismCard = ({ children, ...props }) => {
  const cardBg = useColorModeValue(
    "rgba(255, 255, 255, 0.8)",
    "rgba(26, 32, 44, 0.8)",
  );
  const borderColor = useColorModeValue(
    "rgba(255, 255, 255, 0.2)",
    "rgba(255, 255, 255, 0.1)",
  );

  return (
    <Box
      bg={cardBg}
      backdropFilter="blur(20px)"
      borderRadius="20px"
      border="1px solid"
      borderColor={borderColor}
      boxShadow="xl"
      position="relative"
      overflow="hidden"
      _before={{
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        borderRadius: "20px",
        padding: "1px",
        background: `linear-gradient(135deg, ${borderColor}, transparent)`,
        mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
        maskComposite: "subtract",
        pointerEvents: "none",
      }}
      {...props}
    >
      {children}
    </Box>
  );
};

const PayrunPayouts = () => {
  const navigate = useNavigate();
  const { payrunId } = useParams();
  const setToast = useSetRecoilState(toastState);
  const [payrunData, setPayrunData] = useState(null);

  const textColor = useColorModeValue("gray.700", "white");
  const subTextColor = useColorModeValue("gray.500", "gray.400");
  const bgColor = useColorModeValue("gray.50", "gray.900");

  const [{ loading }, fetchPayrun] = useAxios(
    {
      url: `/api/payrun/${payrunId}`,
      method: "GET",
    },
    { manual: true },
  );

  const fetchPayrunData = useCallback(async () => {
    try {
      const response = await fetchPayrun();
      const responseData = response.data.data;

      if (responseData?.payrunData && responseData.payrunData.length > 0) {
        const payrun = responseData.payrunData[0];
        const payouts = responseData.payoutsList || [];

        // Calculate totals
        const totals = payouts.reduce(
          (acc, payout) => ({
            gross: acc.gross + (payout.gross_amount || 0),
            net: acc.net + (payout.net_amount || 0),
            count: acc.count + 1,
          }),
          { gross: 0, net: 0, count: 0 },
        );

        // Parse date - check for different field names (date, Date, created_at, etc.)
        const dateField = payrun.date || payrun.Date || payrun.created_at;
        const payrunDate = dateField ? new Date(dateField) : new Date();

        setPayrunData({
          month: payrunDate.toLocaleString("en-US", {
            month: "long",
          }),
          year: payrunDate.getFullYear(),
          payrun_id: payrun.id || payrun.Id,
          payouts: payouts,
          total_gross_amount: totals.gross,
          total_net_amount: totals.net,
          payout_count: totals.count,
        });
      }
    } catch (error) {
      logger.error("Error fetching payrun data:", error);
      if (error?.code !== "ERR_CANCELED") {
        setToast({
          isOpen: true,
          title: "Error Occurred",
          status: "error",
          description:
            error?.response?.data?.error || "Failed to fetch payrun data",
        });
      }
    }
  }, [fetchPayrun, setToast]);

  useEffect(() => {
    if (payrunId) {
      fetchPayrunData();
    }
  }, [payrunId, fetchPayrunData]);

  if (loading) {
    return (
      <Box px={{ base: 4, md: 6 }} bg={bgColor} minH="100vh">
        <Center h="400px">
          <VStack spacing={4}>
            <Spinner size="xl" color="orange.500" thickness="4px" />
            <Text color={textColor}>Loading payrun data...</Text>
          </VStack>
        </Center>
      </Box>
    );
  }

  if (!payrunData) {
    return (
      <Box px={{ base: 4, md: 6 }} bg={bgColor} minH="100vh">
        <VStack spacing={4} py={12}>
          <Text fontSize="lg" color={subTextColor}>
            No payrun data available
          </Text>
          <Button
            leftIcon={<Icon as={FaArrowLeft} />}
            onClick={() => navigate("/admin/payouts")}
            colorScheme="orange"
            variant="outline"
          >
            Back
          </Button>
        </VStack>
      </Box>
    );
  }

  const {
    month,
    year,
    payrun_id,
    payouts = [],
    total_gross_amount,
    total_net_amount,
    payout_count,
  } = payrunData;

  return (
    <Box px={{ base: 4, md: 6 }} bg={bgColor} minH="100vh">
      <MotionBox
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        w="100%"
      >
        <VStack spacing={6} align="stretch">
          {/* Breadcrumb */}
          <Breadcrumb
            spacing={2}
            separator={
              <Icon as={FaChevronRight} color={subTextColor} boxSize={3} />
            }
            fontSize="sm"
          >
            <BreadcrumbItem>
              <BreadcrumbLink
                onClick={() => navigate("/admin/payouts")}
                color="#f57d36"
                _hover={{ textDecoration: "underline" }}
              >
                Payouts
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem isCurrentPage>
              <BreadcrumbLink color={textColor}>
                {month} {year}
              </BreadcrumbLink>
            </BreadcrumbItem>
          </Breadcrumb>

          {/* Header */}
          <Flex justify="space-between" align="center">
            <HStack spacing={4}>
              <Button
                leftIcon={<Icon as={FaArrowLeft} />}
                onClick={() => navigate("/admin/payouts")}
                variant="ghost"
                colorScheme="orange"
              >
                Back
              </Button>
              <Divider orientation="vertical" h="30px" />
              <VStack align="start" spacing={0}>
                <Text fontSize="2xl" fontWeight="bold" color={textColor}>
                  Payouts for {month} {year}
                </Text>
                <Text fontSize="sm" color={subTextColor}>
                  Payrun ID: #{payrun_id}
                </Text>
              </VStack>
            </HStack>
            <PayrunExportButton
              payrunId={payrun_id}
              month={month}
              year={year}
            />
          </Flex>

          {/* Summary Cards */}
          <GlassMorphismCard p={6}>
            <VStack spacing={4} align="stretch">
              <Text fontSize="md" fontWeight="600" color={textColor}>
                Payrun Summary
              </Text>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                <Stat>
                  <StatLabel color={subTextColor} fontSize="sm">
                    <HStack spacing={2}>
                      <Icon as={FaUsers} color="blue.500" boxSize={4} />
                      <Text>Total Payouts</Text>
                    </HStack>
                  </StatLabel>
                  <StatNumber color="blue.500" fontSize="2xl">
                    {payout_count}
                  </StatNumber>
                  <StatHelpText color={subTextColor}>
                    Individual payout records
                  </StatHelpText>
                </Stat>

                <Stat>
                  <StatLabel color={subTextColor} fontSize="sm">
                    <HStack spacing={2}>
                      <Icon
                        as={FaMoneyBillWave}
                        color="purple.500"
                        boxSize={4}
                      />
                      <Text>Total Gross Amount</Text>
                    </HStack>
                  </StatLabel>
                  <StatNumber color="purple.500" fontSize="2xl">
                    ₹
                    {total_gross_amount?.toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                    }) || "0.00"}
                  </StatNumber>
                  <StatHelpText color={subTextColor}>
                    Before deductions
                  </StatHelpText>
                </Stat>

                <Stat>
                  <StatLabel color={subTextColor} fontSize="sm">
                    <HStack spacing={2}>
                      <Icon
                        as={FaMoneyBillWave}
                        color="green.500"
                        boxSize={4}
                      />
                      <Text>Total Net Amount</Text>
                    </HStack>
                  </StatLabel>
                  <StatNumber color="green.500" fontSize="2xl">
                    ₹
                    {total_net_amount?.toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                    }) || "0.00"}
                  </StatNumber>
                  <StatHelpText color={subTextColor}>
                    Final payout amount
                  </StatHelpText>
                </Stat>
              </SimpleGrid>
            </VStack>
          </GlassMorphismCard>

          {/* Payouts Table */}
          <PayrunPayoutsTable
            payouts={payouts}
            payrunId={payrun_id}
            month={month}
            year={year}
            totalGrossAmount={total_gross_amount}
            totalNetAmount={total_net_amount}
            payoutCount={payout_count}
          />
        </VStack>
      </MotionBox>
    </Box>
  );
};

export default PayrunPayouts;
